/**
 * Registers <slide-show> for hero/carousel sections (slideshow, compact-slideshow, etc.).
 * Must load after window.SwiperSlide is defined (theme-scripts.liquid).
 *
 * Init runs in connectedCallback so .swiper-wrapper / .swiper-slide exist (constructor runs
 * before children when the tag is parsed or when sections are injected).
 */
if (!customElements.get('slide-show')) {
  class SlideShow extends HTMLElement {
    constructor() {
      super();
      this.initMainSlider = null;
      this.isFirstLoad = true;
      this._onResize = () => this.toggleGridOnResize();
    }

    connectedCallback() {
      if (this._slideShowConnected) return;

      let attempts = 0;
      const maxAttempts = 120;

      const start = () => {
        if (!this.isConnected || this._slideShowConnected) return;
        attempts += 1;
        if (attempts > maxAttempts) {
          console.warn('[slide-show] Bootstrap timed out (wrapper slides or SwiperSlide missing).');
          return;
        }
        if (typeof window.SwiperSlide !== 'function') {
          requestAnimationFrame(start);
          return;
        }

        const wrapper = this.querySelector('.swiper-wrapper');
        const slides = this.querySelectorAll('.swiper-slide');
        if (this.dataset.swiper && (!wrapper || slides.length === 0)) {
          requestAnimationFrame(start);
          return;
        }

        this._slideShowConnected = true;
        this.section = this.closest('section');
        this.mainslide = this.querySelector('[data-main-slide]');
        this.thumbs = this.querySelector('[data-has-thumbs]');

        try {
          this.init();
          this.updateSliderNavigation();
          this.handleValues();
          this.toggleGridOnResize();
        } catch (err) {
          console.warn('[slide-show] Init failed', err);
          this._slideShowConnected = false;
          return;
        }
        window.addEventListener('resize', this._onResize);
      };

      requestAnimationFrame(start);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      this._slideShowConnected = false;
      this._shopifyEditorHooks = false;
      if (this.initMainSlider?.destroy) {
        try {
          this.initMainSlider.destroy(true, true);
        } catch (_) {}
        this.initMainSlider = null;
      }
    }

    toggleGridOnResize() {
      const desktopGrid = this.querySelector('[data-grid-desktop]');
      const mobileGrid = this.querySelector('[data-grid-mobile]');
      const isMobile = window.innerWidth <= 768;

      if (!desktopGrid && !mobileGrid) return;

      if (isMobile) {
        desktopGrid?.classList.add('hidden');
        mobileGrid?.classList.remove('hidden');
      } else {
        mobileGrid?.classList.add('hidden');
        desktopGrid?.classList.remove('hidden');
      }
    }

    /** Resolve pagination.el to a node inside this element when possible */
    _resolvePaginationEl(config) {
      const pag = config.pagination;
      if (!pag || !pag.el || typeof pag.el !== 'string') return;

      const sel = pag.el.trim();
      const local = this.querySelector(sel);
      const root =
        local ||
        (typeof document !== 'undefined' && document.querySelector ? document.querySelector(sel) : null);
      if (root) pag.el = root;
    }

    init() {
      if (!this.dataset.swiper) return;

      let config;
      try {
        config = JSON.parse(this.dataset.swiper || '{}');
      } catch (e) {
        console.warn('[slide-show] Invalid data-swiper JSON', e);
        return;
      }

      this._resolvePaginationEl(config);

      const slideCount = this.querySelectorAll('.swiper-slide').length;
      if (config.loop && slideCount <= 2) {
        config.loop = false;
        config.rewind = true;
      }

      const originalSwiperSlide = window.SwiperSlide;
      const paginationValue = this.dataset.pagination;

      window.SwiperSlide = function (element, swiperConfig) {
        if (swiperConfig.pagination && swiperConfig.pagination.renderBullet === 'customBullet') {
          swiperConfig.pagination.renderBullet = function (index, className) {
            if (paginationValue === 'numbers') {
              return `<button type="button" class="${className}" aria-label="Go to slide ${index + 1}"><span>${index + 1}</span></button>`;
            }
            if (paginationValue === 'bars') {
              return `<button type="button" class="${className}" aria-label="Go to slide ${index + 1}"><span class="swiper-pagination-bar"></span></button>`;
            }
            const numberMarkup =
              paginationValue === 'numbers'
                ? `<span>${index + 1}</span>`
                : '<svg width="6" height="6" viewBox="0 0 6 6" fill="none" ><circle cx="3" cy="3" r="3" transform="matrix(1 0 0 -1 0 6)" fill="white"/></svg>';

            return `<button type="button" class="${className}" aria-label="Go to slide ${index + 1}">
            <span class="svg-inside">
              <svg class="progress">
                <circle class="circle-origin circle-track" r="15.5" cx="0" cy="0" transform="translate(16 16)"></circle>
                <circle class="circle-origin" r="15.5" cx="0" cy="0" transform="translate(16 16)"></circle>
              </svg>
            </span>
            ${numberMarkup}
        </button>`;
          };
        }

        return originalSwiperSlide(element, swiperConfig);
      };

      this.initMainSlider = window.SwiperSlide(this, config);

      window.SwiperSlide = originalSwiperSlide;

      if (!this.initMainSlider) return;

      requestAnimationFrame(() => {
        try {
          this.initMainSlider?.update?.();
        } catch (_) {}
      });

      const prevIndexElement = this.querySelector('[data-index-slides]');
      if (this.initMainSlider && prevIndexElement) {
        this.initMainSlider.on('slideChange', () => {
          const currentSlide = this.initMainSlider.realIndex + 1;
          prevIndexElement.textContent = currentSlide < 10 ? `0${currentSlide}` : currentSlide;
        });
      }

      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        if (!this._shopifyEditorHooks) {
          this._shopifyEditorHooks = true;

          this.addEventListener('shopify:block:select', (event) => {
            this.handleBlockSelect(event.target);
            this.handleValues();
          });

          this.addEventListener('shopify:section:select', (event) => {
            this.handleBlockSelect(event.target);
            this.handleValues();
          });

          document.addEventListener('shopify:section:load', (event) => {
            this.handleBlockSelect(event.target);
            this.handleValues();
          });
        }
      }
    }

    handleEditorLoad(target) {
      this.handleBlockSelect(target);
    }

    handleBlockSelect(target) {
      const slideIndex = Number(target?.dataset?.swiperSlideIndex);
      if (!isNaN(slideIndex) && this?.initMainSlider?.slideToLoop) {
        this.initMainSlider.slideToLoop(slideIndex);
      } else if (!isNaN(slideIndex) && this?.initMainSlider?.slideTo) {
        this.initMainSlider.slideTo(slideIndex);
      }
      const content = target.querySelector?.('[data-slideshow-content]');
      if (content) {
        content.classList.add('smart-grid-editor');
      }
    }

    handleBlockDeselect(target) {
      const content = target.querySelector('[data-slideshow-content]');
      if (content?.classList.contains('smart-grid-editor')) {
        content.classList.remove('smart-grid-editor');
      }
    }

    handleBlockselect(target) {
      const content = target.querySelector('[data-slideshow-content]');
      content.classList.add('smart-grid-editor');
    }

    updateSliderNavigation() {
      if (!this.querySelector('[data-slider-navigation]')) return;
      this.updateSliderHeightNavigation =
        this.querySelector('[data-slider-navigation]').getBoundingClientRect().height;
      document.body.style.setProperty('--slideshow-nav--height', `${this.updateSliderHeightNavigation}px`);
    }

    handleValues() {
      const headings = this.querySelectorAll('[data-heading-row]');
      const descriptions = this.querySelectorAll('[data-description-row]');
      const buttons = this.querySelectorAll('[data-button-row]');

      const headingsMb = this.querySelectorAll('[data-mbheading-row]');
      const descriptionsMb = this.querySelectorAll('[data-mbdescription-row]');
      const buttonsMb = this.querySelectorAll('[data-mbbutton-row]');

      const headingsIp = this.querySelectorAll('[data-ipheading-row]');
      const descriptionsIp = this.querySelectorAll('[data-ipdescription-row]');
      const buttonsIp = this.querySelectorAll('[data-ipbutton-row]');

      const getRows = (nodeList, key) =>
        Array.from(nodeList)
          .map((el) => Number(el.dataset[key]))
          .filter((n) => !isNaN(n));

      const rows = [
        ...getRows(headings, 'headingRow'),
        ...getRows(descriptions, 'descriptionRow'),
        ...getRows(buttons, 'buttonRow'),
      ];

      const rowsMb = [
        ...getRows(headingsMb, 'mbheadingRow'),
        ...getRows(descriptionsMb, 'mbdescriptionRow'),
        ...getRows(buttonsMb, 'mbbuttonRow'),
      ];

      const rowsIp = [
        ...getRows(headingsIp, 'ipheadingRow'),
        ...getRows(descriptionsIp, 'ipdescriptionRow'),
        ...getRows(buttonsIp, 'ipbuttonRow'),
      ];

      const section = this.closest('.shopify-section');
      if (!section) return;

      requestAnimationFrame(() => {
        if (rowsMb.length) {
          section.style.setProperty('--smart-grid-dynamic-rows-mobile', `max(${rowsMb.join(', ')})`);
        }
        if (rowsIp.length) {
          section.style.setProperty('--smart-grid-dynamic-rows-ip', `max(${rowsIp.join(', ')})`);
        }

        if (rows.length) {
          section.style.setProperty('--smart-grid-dynamic-rows', `max(${rows.join(', ')})`);
        }
      });
    }
  }

  customElements.define('slide-show', SlideShow);
}
